import {
  MenuItem,
  Tooltip,
  IconButton,
  Paper,
  Popper,
  MenuList,
  ClickAwayListener,
  Box,
} from '@mui/material';
import { useEffect, useContext, useState } from 'react';
import { useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { AppState, AppDispatch } from '../store';
import {
  getLanguageCode,
  getProductId,
  IsAppOpenedInIframe,
  IsAppOpenedByExtApp,
} from '../services';
import { useTranslation } from 'react-i18next';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LanguageIcon from '@mui/icons-material/Language';
import type { IChangeLanguageProps, ILanguage, IPreferenceLanguageProps } from '../../types';
import {
  DefaultLanguage,
  ApplicationSupportedLanguages,
  PopperModifier,
  ESessionStore,
  EPageNames,
  ESalesTextType,
} from '../data/Constants';
import { AppAuthContext } from '../services/Contexts';
import { SessionStore } from '../services/SessionStore';
import { IsDefaultFlowInSessionStore } from '../services/SessionStoreHelperFunctions';
import { setUserSettings } from '../store/states/UserSettingsSlice';
import { setUserPreferences } from '../store/states/UserPreferencesSlice';
import {
  getSalesText,
  resetSalesText,
  updateSubmodelSalesText,
} from '../store/states/ConfigurationSlice';

/**
 * This method will trigger when change the language in the user Preference dialog
 * This method will update the current language translation and call the sales text Api
 * @param {IPreferenceLanguageProps} props change language props
 * @returns {void}
 */
async function onPreferenceLangChange(props: IPreferenceLanguageProps): Promise<void> {
  const { location, changeLanguage, token, userPreferences, dispatch } = props;
  if (userPreferences.preferenceChanged) {
    //reset the existing salestext to get the salestext for the current user preference language
    dispatch(resetSalesText());
    const langCode = getLanguageCode(location.pathname, true);
    if (langCode) {
      dispatch(setUserSettings({ language: langCode }));
      changeLanguage(langCode);
    }
    dispatch(setUserPreferences({ preferenceChanged: false }));
    if (location.pathname !== EPageNames.LandingPage) {
      const mandatoryPromises = [
        dispatch(
          getSalesText({
            language: langCode,
            token: token,
            modelId: getProductId(),
            apiType: ESalesTextType.Short,
            showError: false,
          })
        ),
        dispatch(
          getSalesText({
            language: langCode,
            token: token,
            modelId: getProductId(),
            apiType: ESalesTextType.Long,
            showError: false,
          })
        ),
      ];
      await Promise.allSettled(mandatoryPromises);
      dispatch(updateSubmodelSalesText({ language: langCode })); // Updates sales text for unassigned submodels in Redux state
    }
  } else {
    dispatch(setUserSettings({ language: userPreferences.language }));
    dispatch(updateSubmodelSalesText({ language: userPreferences.language })); // Updates sales text for unassigned submodels in Redux state
    changeLanguage(userPreferences.language);
  }
}

/**
 * This method will trigger when change the language in language dropdown in action panel
 * This method will update the current language translation and call the sales text Api
 * @param {string} value current language value
 * @param {IChangeLanguageProps} handleLanguageProps change language props
 * @returns {void}
 */
const onHandleLanguage = async (
  value: string,
  handleLanguageProps: IChangeLanguageProps
): Promise<void> => {
  const { availableLanguages, changeLanguage, handleClose, token, dispatch } = handleLanguageProps;
  //reset the existing salestext to get the salestext for the current language
  dispatch(resetSalesText());
  const lang = availableLanguages.find((_: ILanguage) => _.code === value);

  if (lang?.code) {
    changeLanguage(lang?.code);
  }

  handleClose();
  dispatch(setUserSettings({ language: lang?.code }));
  if (
    token &&
    location.pathname !== EPageNames.LandingPage &&
    location.pathname !== EPageNames.Logout
  ) {
    const bcpLangCode = lang?.code;
    const productModel = getProductId();

    if (IsAppOpenedInIframe() || IsAppOpenedByExtApp()) {
      SessionStore.set(ESessionStore.Language, lang?.code.split('-')[0]);
      SessionStore.set(ESessionStore.Country, lang?.code.split('-')[1]);
    }
    const mandatoryPromises = [
      dispatch(
        getSalesText({
          language: bcpLangCode ?? '',
          token: token,
          modelId: productModel,
          apiType: 'short',
          showError: false,
        })
      ),
      dispatch(
        getSalesText({
          language: bcpLangCode ?? '',
          token: token,
          modelId: productModel,
          apiType: 'long',
          showError: false,
        })
      ),
    ];
    await Promise.allSettled(mandatoryPromises);
    dispatch(updateSubmodelSalesText({ language: lang?.code })); // Updates sales text for all submodels in Redux state. This should be called after fetching sales text for model to ensure submodel sales text is updated.
  }
};

const handleLangCode = (
  language: string,
  changeLanguage: (language: string) => Promise<void>
): void => {
  // to convert the page into desired lang for LandingPage when click of home button
  if (location.pathname === EPageNames.LandingPage && IsDefaultFlowInSessionStore()) {
    changeLanguage(language);
  }
};

/**
 * Renders the ViewHandler dropdown where the user can change the view
 * @returns {JSX.Element} the viewHandler Element
 */
export const LanguageSetting = () => {
  const token = useContext(AppAuthContext);
  const location = useLocation();
  const { i18n, t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const id = open ? 'scroll-playground' : undefined;
  const [arrowRef, setArrowRef] = useState(null);

  //selectors
  const featureFlags = useSelector((state: AppState) => state.claimsData.featureFlags);
  const userLanguage = useSelector((state: AppState) => state.userSettings.language);
  const userPreferences = useSelector((state: AppState) => state.userPreferences);
  const configuration = useSelector((state: AppState) => state.configuration);

  const dispatch = useDispatch<AppDispatch>();
  const languageRender = featureFlags?.Language;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handlePreferenceLangChangeAsync = async () => {
    await onPreferenceLangChange({ location, changeLanguage, token, userPreferences, dispatch });
  };
  useEffect(() => {
    const langCode = getLanguageCode(location.pathname);
    if (langCode) {
      changeLanguage(langCode);
    }
  }, []);

  useEffect(() => {
    handlePreferenceLangChangeAsync();
  }, [userPreferences.language]);

  useEffect(() => {
    handleLangCode(userLanguage, changeLanguage);
  }, [JSON.stringify(configuration.pmLanguages), location.pathname]);

  const changeLanguage = async (lng: string) => {
    try {
      i18n.changeLanguage(lng.replace('-', '_'));
    } catch (err) {
      console.log(err);
    }
  };
  let availableLanguages: ILanguage[] = configuration?.pmLanguages ?? DefaultLanguage.Value;
  if (
    location.pathname === EPageNames.LandingPage ||
    location.pathname === EPageNames.Logout ||
    location.pathname === EPageNames.CloseTab ||
    location.pathname === EPageNames.Notified
  ) {
    availableLanguages = ApplicationSupportedLanguages;
  }
  if (!languageRender?.active || !languageRender?.visible) {
    return null;
  }
  return (
    <>
      <Tooltip title={t('userPreference.language')}>
        <span>
          <IconButton
            disabled={
              availableLanguages.length <= 1 ||
              !IsDefaultFlowInSessionStore() ||
              (languageRender && !languageRender?.editable)
            }
            className={`languageContainer icon-font-size ${anchorEl ? 'activeIcon' : ''}`}
            onClick={handleClick}
            aria-describedby={id}
            data-testid="btn-language"
          >
            <LanguageIcon fontSize="small" />
            {`${getLanguageCode(location.pathname)}`}
            <KeyboardArrowDownIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Popper
        className="zindex-100"
        id={id}
        open={open}
        anchorEl={anchorEl}
        placement="bottom"
        role="tooltip"
        modifiers={[
          ...PopperModifier,
          {
            name: 'arrow',
            enabled: true,
            options: {
              element: arrowRef,
            },
          },
        ]}
      >
        <div role="tooltip">
          <Box
            component="div"
            id="arrow"
            data-popper-arrow
            className="popperStyle arrow"
            ref={setArrowRef}
          />
          <Box>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  id="language-menu"
                  aria-labelledby="language-button"
                  data-testid="menu-language"
                >
                  {availableLanguages.map(
                    (value: ILanguage) =>
                      userLanguage !== value.code && (
                        <MenuItem
                          onClick={() => {
                            onHandleLanguage(value.code, {
                              availableLanguages,
                              changeLanguage,
                              handleClose,
                              token,
                              dispatch,
                            }).catch(console.error);
                          }}
                          value={value.name}
                          key={value.code}
                          data-testid="userSettings-list"
                        >
                          {value.name}&nbsp;({value.code})
                        </MenuItem>
                      )
                  )}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Box>
        </div>
      </Popper>
    </>
  );
};
